/**
 * 画布 AI 配置 Tab（管理后台 → 画布分组）
 *
 * 画布 LLM 能力开关与模型选择（canvas_system_config）。
 * 配置已从"提示词优化"独立，两者互不影响。
 */

import { useEffect, useState } from 'react';
import { Loader2, Save } from 'lucide-react';
import toast from '../../Toast';
import { api, type CanvasSystemConfig } from '../../../services/api';
import { llmPlatformAPI, type LlmModel } from '../../../baselines/llm/llmPlatformAPI';

export default function CanvasAIConfigTab() {
  const [config, setConfig] = useState<CanvasSystemConfig | null>(null);
  const [models, setModels] = useState<LlmModel[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    Promise.all([
      api.getCanvasSystemConfig().then((res) => res.data),
      llmPlatformAPI.listModels().then((res) => res.data).catch(() => [] as LlmModel[]),
    ])
      .then(([cfg, modelList]) => {
        setConfig(cfg);
        setModels(modelList.filter((m) => m.isActive));
      })
      .catch((err) => toast.error(err instanceof Error ? err.message : '加载失败'))
      .finally(() => setLoading(false));
  }, []);

  const handleSave = async () => {
    if (!config) return;
    setSaving(true);
    try {
      const res = await api.updateCanvasSystemConfig({
        isEnabled: config.isEnabled,
        model: config.model,
        temperature: config.temperature,
        maxTokens: config.maxTokens,
      });
      setConfig(res.data);
      toast.success('已保存');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : '保存失败');
    } finally {
      setSaving(false);
    }
  };

  if (loading || !config) {
    return (
      <div className="flex items-center justify-center py-12 text-light-muted">
        <Loader2 className="w-5 h-5 animate-spin mr-2" />
        加载中
      </div>
    );
  }

  return (
    <div className="space-y-5 max-w-3xl">
      <div className="card p-5 space-y-5">
        <div>
          <h3 className="text-base font-semibold mb-1">画布 AI 配置</h3>
          <p className="text-xs text-light-muted">
            画布 LLM 节点与画布助手使用的模型能力。此配置独立于"AI 提示词优化"，互不影响。
          </p>
        </div>

        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={config.isEnabled}
            onChange={(e) => setConfig((prev: CanvasSystemConfig | null) => (prev ? { ...prev, isEnabled: e.target.checked } : prev))}
            className="w-4 h-4 rounded"
          />
          <span className="text-sm text-light-text">启用画布 AI 功能</span>
        </label>

        <div>
          <label className="block text-sm font-medium mb-1">LLM 模型</label>
          <select
            value={config.model}
            onChange={(e) => setConfig((prev: CanvasSystemConfig | null) => (prev ? { ...prev, model: e.target.value } : prev))}
            className="w-full max-w-md input text-sm"
          >
            <option value="">未选择（启用后画布 AI 将不可用）</option>
            {models.map((m) => (
              <option key={m.id} value={m.id}>
                {m.name}（{m.modelIdString}）
              </option>
            ))}
          </select>
          <p className="text-xs text-light-muted mt-1">模型列表来自「LLM 平台」中已启用的模型。</p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-md">
          <div>
            <label className="block text-sm font-medium mb-1">
              temperature
              <span className="text-xs text-light-muted ml-1">（0-2）</span>
            </label>
            <input
              type="number"
              min={0}
              max={2}
              step={0.1}
              value={config.temperature}
              onChange={(e) =>
                setConfig((prev: CanvasSystemConfig | null) => (prev ? { ...prev, temperature: Number(e.target.value) || 0.7 } : prev))
              }
              className="w-full input text-sm"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">
              maxTokens
              <span className="text-xs text-light-muted ml-1">（64-8192）</span>
            </label>
            <input
              type="number"
              min={64}
              max={8192}
              step={64}
              value={config.maxTokens}
              onChange={(e) =>
                setConfig((prev: CanvasSystemConfig | null) => (prev ? { ...prev, maxTokens: parseInt(e.target.value, 10) || 1024 } : prev))
              }
              className="w-full input text-sm"
            />
          </div>
        </div>
      </div>

      <div>
        <button
          onClick={handleSave}
          disabled={saving}
          className="btn-primary flex w-full items-center justify-center gap-2 px-5 py-2 sm:w-auto"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          保存配置
        </button>
      </div>
    </div>
  );
}
